// Dashboard QUICK FILTER mode — the chips and the mode's own semantics.
//
// Quick filter is an OPT-IN mode: the user turns it on, and from then on a
// single click on any visual element (a bar, a slice, an axis category, a table
// row) filters EVERY tile of the dashboard by the clicked column = clicked
// value. The mode is explicit on purpose — in other BI tools cross-filtering is
// always armed and applies silently, which is exactly the confusion this design
// avoids: nothing filters until the mode is on, every applied filter shows as a
// named removable chip, and each tile reports whether the filter reached it.
//
// The chips are only an EXPRESSION BUILDER. The filtering itself is the visual's
// own capability: `chipsToFilterExpression` turns the chips into the shared
// FilterExpression (visualFilter.ts) and each <InsightVisual> evaluates it
// against the data it has already loaded — the same mechanism its ⋮ "Filter"
// dialog uses. So there is ONE filter implementation, no re-query, and a tile
// that cannot evaluate a column reports it (see `tileQuickFilterNotice`) instead
// of looking filtered.
//
// This module owns the chip shapes + the pure state transitions; the chrome
// lives in QuickFilterBar.tsx and the click plumbing in InsightVisual.tsx. A
// host surface (designer preview, smart-page embed, runtime client) holds the
// chip array, passes `filter={chipsToFilterExpression(chips)}` to every tile,
// and collects each tile's `onFilterResult` for the notices.

import { mapVisualData, pickGroup } from './dataUtils'
import { formatCell } from './formatters'
import {
  FILTER_REASON_NOT_IN_DATA, FILTER_REASON_NO_ROWS, FILTER_REASON_TRUNCATED,
} from './visualFilter'
import type { FilterCondition, FilterExpression, FilterResult } from './visualFilter'
import type { VisualJsonData, VisualLayout } from './types'

/** One value held by a chip: the RAW value (matched against rows) + its display text. */
export interface QuickFilterValue {
  value: unknown
  display: string
}

/** One quick filter: a column and the value(s) selected for it. */
export interface QuickFilterChip {
  /** Column NAME — what every tile's dataset is matched on. */
  column: string
  /** Human label from the source visual's layout (falls back to the name). */
  label: string
  values: QuickFilterValue[]
  /** Tile the click came from (shown in the chip tooltip, purely informational). */
  sourceVisual?: string
}

/** One column/value pair derived from a clicked point, before it becomes a chip. */
export interface QuickFilterPick {
  column: string
  label: string
  value: unknown
  display: string
}

/** What one tile did with the active chips — a visual's own FilterResult, plus
 *  the cases only the host can report (a report tile, a tile that failed). */
export type TileQuickFilterStatus = Pick<FilterResult, 'applied' | 'skipped'>

/** Reasons the HOST reports for tiles that never evaluated the expression. */
export const REASON_REPORT_TILE = 'report_tile'
export const REASON_TILE_ERROR = 'tile_error'

/** Machine reason → the sentence a user reads on the tile. Covers the
 *  evaluator's reasons (visualFilter.ts) and the host's two. */
export const SKIP_REASON_TEXT: Record<string, string> = {
  [FILTER_REASON_NOT_IN_DATA]: 'this visual\u2019s data has no column of that name',
  [FILTER_REASON_NO_ROWS]: 'this visual has no data to filter',
  [FILTER_REASON_TRUNCATED]: 'this visual shows only the first rows of its data, so filtering it would report a partial total',
  [REASON_REPORT_TILE]: 'report tiles run their own query, so a quick filter cannot reach them',
  [REASON_TILE_ERROR]: 'this tile did not load',
}

// Raw-value equality with a string fallback — mirrors drilldown's rawEq, so a
// numeric 10 and the string '10' coming from different renderers are the same
// selection.
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true
  return String(a ?? '') === String(b ?? '')
}

/**
 * The column/value pairs behind a clicked point: every visible, non-calc
 * DIMENSION column present in the row (measures are never filterable — you
 * cannot meaningfully filter a dashboard by "Revenue = 41,233").
 *
 * A click can legitimately yield SEVERAL pairs (a stacked bar carries both its
 * category and its series; a table row carries every dimension column). Each
 * becomes its own chip so the user sees — and can drop — exactly what was
 * applied, rather than one opaque compound filter.
 */
export function quickFilterPicksFromRow(
  row: Record<string, unknown>,
  visualLayout: VisualLayout,
): QuickFilterPick[] {
  const md = mapVisualData([{}, row] as unknown as VisualJsonData, visualLayout)
  // A measure does not live only in the "values" group: a map colours by
  // `color`, sizes bubbles by `size`, a heat layer weighs by `weight`. Those
  // carry an aggregated NUMBER, so clicking a region must not offer to filter by
  // it ("Sales = 41,233"). Membership alone is not enough either — `color`
  // legitimately holds a categorical series on other charts — so a column in one
  // of those groups counts as a measure only when it also LOOKS like one: it
  // carries an aggregate function, or the clicked value is a number. That keeps
  // a numeric DIMENSION (a year, a store number) filterable, which is why this
  // is not simply "skip every number".
  //
  // Note the column TYPE is not available here: this maps against an empty
  // schema (`[{}, row]`), so `c.type` is always undefined — the value in hand is
  // the only evidence of numeric-ness.
  const looksNumeric = (v: unknown) =>
    typeof v === 'number'
    || (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)))
  const valueNames = new Set(
    pickGroup(md, 'values', 'value', 'measures', 'measure', 'size', 'weight', 'color')
      .filter((c) => Boolean(c.agg) || looksNumeric(row[c.name]))
      .map((c) => c.name),
  )
  const out: QuickFilterPick[] = []
  const seen = new Set<string>()
  for (const group of Object.values(md.groups)) {
    for (const c of group) {
      if (c.hidden || c.isCalc || valueNames.has(c.name)) continue
      if (seen.has(c.name)) continue
      if (!(c.name in row)) continue
      const value = row[c.name]
      // A null/blank dimension is not a usable filter — it would match "no
      // value" across every tile, which is never what clicking a chart means.
      if (value === undefined || value === null || value === '') continue
      seen.add(c.name)
      const display = formatCell(value, {
        type: c.type, coltype: c.coltype, format: c.format, pattern: c.pattern,
      })
      out.push({
        column: c.name,
        label: c.label || c.name,
        value,
        display: display !== '' ? String(display) : String(value),
      })
    }
  }
  return out
}

/**
 * Fold clicked picks into the current chips.
 *
 * Plain click  → the clicked column's chip is REPLACED with the new value
 *                (clicking East then West shows West, not both).
 * Additive     → (ctrl / ⌘ held) the value joins the column's existing chip,
 *                and clicking an already-selected value removes it — the
 *                standard multi-select gesture. Removing the last value drops
 *                the chip entirely.
 */
export function mergeQuickFilterPicks(
  chips: QuickFilterChip[],
  picks: QuickFilterPick[],
  opts: { additive?: boolean; sourceVisual?: string } = {},
): QuickFilterChip[] {
  if (!picks.length) return chips
  const next = chips.map((c) => ({ ...c, values: [...c.values] }))
  for (const pick of picks) {
    const idx = next.findIndex((c) => c.column === pick.column)
    const entry: QuickFilterValue = { value: pick.value, display: pick.display }
    if (idx < 0) {
      next.push({
        column: pick.column,
        label: pick.label,
        values: [entry],
        sourceVisual: opts.sourceVisual,
      })
      continue
    }
    if (!opts.additive) {
      next[idx] = { ...next[idx], values: [entry], sourceVisual: opts.sourceVisual }
      continue
    }
    const hit = next[idx].values.findIndex((v) => sameValue(v.value, pick.value))
    if (hit >= 0) next[idx].values.splice(hit, 1)
    else next[idx].values.push(entry)
  }
  return next.filter((c) => c.values.length > 0)
}

/** Drop one value from a chip (or the whole chip when `value` is omitted). */
export function removeQuickFilterValue(
  chips: QuickFilterChip[],
  column: string,
  value?: unknown,
): QuickFilterChip[] {
  return chips
    .map((c) => {
      if (c.column !== column) return c
      if (value === undefined) return { ...c, values: [] }
      return { ...c, values: c.values.filter((v) => !sameValue(v.value, value)) }
    })
    .filter((c) => c.values.length > 0)
}

/** Stable key for the chip set — hosts use it to memoize per-tile filtering. */
export function quickFilterKey(chips: QuickFilterChip[]): string {
  return chips
    .filter((c) => c.column && c.values.length)
    .map((c) => `${c.column}=${c.values.map((v) => String(v.value)).join('|')}`)
    .sort()
    .join(';')
}

/** "Region: East" / "Region: East, West" — the chip's own caption. */
export function chipCaption(chip: QuickFilterChip): string {
  return `${chip.label}: ${chip.values.map((v) => v.display).join(', ')}`
}

// ── Chips → a filter expression ────────────────────────────────────────────

/**
 * Turn the chips into the filter EXPRESSION each visual evaluates itself.
 *
 * One rule per chip, AND-ed together (values inside a chip OR via `in`), so the
 * host hands the same expression to every tile and each visual decides what it
 * can do with it (see visualFilter.applyFilterToData). Returns null when no chip
 * carries a value — a visual given null filters nothing.
 */
export function chipsToFilterExpression(
  chips: QuickFilterChip[],
): FilterExpression | null {
  const rules: FilterCondition[] = (chips || [])
    .filter((c) => c.column && c.values.length)
    .map((c) => ({
      column: c.column,
      operator: c.values.length > 1 ? 'in' : '=',
      values: c.values.map((v) => v.value),
    }))
  return rules.length ? { condition: 'and', rules } : null
}

/**
 * What a tile should say about the active quick filters — the FULL picture,
 * including the positive case.
 *
 * A dashboard where some tiles honour a filter and others cannot is only honest
 * if BOTH are marked: an unmarked tile is indistinguishable from one that
 * silently ignored the click, which is the failure this mode exists to prevent.
 * So every tile gets a state while chips are active:
 *
 *   filtered  — every chip reached it
 *   partial   — some chips reached it, some could not
 *   none      — no chip reached it; its numbers are the unfiltered ones
 *
 * `skipped` carries a per-column sentence (each column can be skipped for its
 * OWN reason) so the tile's hover explains itself rather than showing only the
 * first reason. Returns null when no filter is applied — nothing to report.
 */
export interface TileFilterIndication {
  state: 'filtered' | 'partial' | 'none'
  /** Short badge caption. */
  label: string
  /** Chip labels this tile evaluated. */
  applied: string[]
  /** Chip labels it could not, each with the reason in plain words. */
  skipped: Array<{ label: string; why: string }>
  /** One-line summary — the `title` fallback where no popover is rendered. */
  text: string
}

export function tileQuickFilterIndication(
  status: TileQuickFilterStatus | undefined,
  chips: QuickFilterChip[],
): TileFilterIndication | null {
  if (!chips.length) return null
  // No result reported yet (the tile has not rendered) — say nothing rather
  // than flashing "not filtered" before it has had its chance.
  if (!status) return null
  const labelFor = (col: string) => chips.find((c) => c.column === col)?.label || col
  const applied = (status.applied ?? []).map(labelFor)
  const skipped = (status.skipped ?? []).map((s) => ({
    label: labelFor(s.column),
    why: SKIP_REASON_TEXT[s.reason] || 'this visual cannot be filtered by it',
  }))
  if (!applied.length && !skipped.length) return null

  const state: TileFilterIndication['state'] =
    !skipped.length ? 'filtered' : applied.length ? 'partial' : 'none'
  const label =
    state === 'filtered' ? 'filtered' : state === 'partial' ? 'partly filtered' : 'not filtered'
  const parts: string[] = []
  if (applied.length) parts.push(`Filtered by ${applied.join(', ')}.`)
  for (const s of skipped) parts.push(`Not filtered by ${s.label} — ${s.why}.`)
  return { state, label, applied, skipped, text: parts.join(' ') }
}

/**
 * The pre-indication shape: only the tiles that could NOT honour a filter.
 * Superseded by `tileQuickFilterIndication` (which also reports the positive
 * case); kept because it is the narrower question and cheap to answer.
 */
export function tileQuickFilterNotice(
  status: TileQuickFilterStatus | undefined,
  chips: QuickFilterChip[],
): { tone: 'partial' | 'none'; text: string } | null {
  const ind = tileQuickFilterIndication(status, chips)
  if (!ind || ind.state === 'filtered') return null
  return { tone: ind.state, text: ind.skipped.map((s) => `Not filtered by ${s.label} — ${s.why}.`).join(' ') }
}

/** How many of `total` tiles the chips actually reached (for the bar's summary). */
export function quickFilterReach(
  statuses: Array<TileQuickFilterStatus | undefined>,
): { filtered: number; total: number } {
  let filtered = 0
  for (const s of statuses) {
    if ((s?.applied ?? []).length) filtered += 1
  }
  return { filtered, total: statuses.length }
}
